import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';

interface StatsState {
  authorised_apps: number;
  connected_services: number | string;
  security_score: number;
  last_login: string;
}

const defaultStats: StatsState = {
  authorised_apps: 0,
  connected_services: '—',
  security_score: 0,
  last_login: ''
};

const rtf = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });

const formatRelativeTime = (dateString: string) => {
  const target = new Date(dateString).getTime();
  if (Number.isNaN(target)) return 'Unknown';
  const diffMs = target - Date.now();
  const diffSeconds = Math.round(diffMs / 1000);

  const divisions: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
    { amount: 60, unit: 'second' },
    { amount: 60, unit: 'minute' },
    { amount: 24, unit: 'hour' },
    { amount: 7, unit: 'day' },
    { amount: 4.34524, unit: 'week' },
    { amount: 12, unit: 'month' },
    { amount: Number.POSITIVE_INFINITY, unit: 'year' }
  ];

  let duration = diffSeconds;
  for (const division of divisions) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }

  return rtf.format(Math.round(duration), 'year');
};

const StatsCards = () => {
  const [stats, setStats] = useState<StatsState>(defaultStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    userService
      .getStats()
      .then((response) => setStats(response.data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="card">Loading stats…</div>;
  }

  if (error) {
    return (
      <div className="card" style={{ color: 'var(--danger)' }}>
        Unable to load stats: {error}
      </div>
    );
  }

  const cards = [
    { label: 'Authorised apps', value: stats.authorised_apps },
    { label: 'Connected services', value: stats.connected_services },
    { label: 'Security score', value: `${stats.security_score}%` },
    { label: 'Last login', value: stats.last_login ? formatRelativeTime(stats.last_login) : '—' }
  ];

  return (
    <div className="dashboard-grid">
      {cards.map((card) => (
        <div className="card" key={card.label}>
          <p style={{ color: 'var(--gray)', marginBottom: '0.4rem' }}>{card.label}</p>
          <h2 style={{ margin: 0 }}>{card.value}</h2>
        </div>
      ))}
    </div>
  );
};

export default StatsCards;
